import LZString from 'lz-string';

import type { Filter, FilterId } from '../types/filters';

/**
 * The filter entries a compressed `filters` url param holds, or null when the
 * param is missing or does not hold a list of [filter id, filter] pairs.
 */
export const decodeFiltersParam = (
  compressed: string | null
): [FilterId, Filter][] | null => {
  if (!compressed) return null;
  try {
    const json = LZString.decompressFromEncodedURIComponent(compressed);
    const parsed: unknown = json ? JSON.parse(json) : null;
    return isFilterEntryList(parsed) ? parsed : null;
  } catch {
    // lz-string throws on some malformed input, JSON.parse on the rest.
    return null;
  }
};

/**
 * The filter map a saved view stores, compressed into its url's `filters`
 * param, or null for a set saved in an older format or one that cannot be
 * read. Shared by applying a set and by describing one, so both read a set
 * the same way.
 */
export const decodeSavedViewFilters = (
  url: string
): [FilterId, Filter][] | null =>
  decodeFiltersParam(
    new URLSearchParams(url.split('?')[1] ?? '').get('filters')
  );

const isFilterEntryList = (value: unknown): value is [FilterId, Filter][] =>
  Array.isArray(value) &&
  value.every(
    (entry) =>
      Array.isArray(entry) &&
      entry.length === 2 &&
      typeof entry[0] === 'string' &&
      typeof entry[1] === 'object' &&
      entry[1] !== null
  );
